
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import { iconToFA } from '../../../../../utils/iconHelpers';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

export default function MenuReorderModal({ open, idPadreContexto, idMenuPadre, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: 'Información',
        message: '',
        variant: 'info'
    });

    // Cargar los items hermanos (hijos del mismo padre)
    useEffect(() => {
        if (!open) return;

        const loadItems = async () => {
            setLoading(true);
            try {
                // Obtenemos todos los menús
                // Idealmente deberíamos tener un endpoint para obtener solo hijos, pero usaremos el filtro en cliente por ahora
                // OJO: Si idMenuPadre es 0, son raíces.
                const res = await axios.get(ENDPOINTS.menus);
                const allMenus = (res.data || []).map(m => Models.menu(m));

                // Filtrar por el padre, solo activos
                const children = allMenus.filter(m => Number(m.idPadre || 0) === Number(idMenuPadre || 0) && m.activo);

                // Ordenar por 'orden'
                children.sort((a, b) => (a.orden || 0) - (b.orden || 0));

                setItems(children);
            } catch (error) {
                console.error("Error cargando menús para reordenar:", error);
                setAlertDialog({
                    open: true,
                    title: 'Error',
                    message: "Error al cargar la lista de menús.",
                    variant: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadItems();
    }, [open, idMenuPadre]);

    const handleDragStart = (e, index) => {
        setDraggedItemIndex(index);
        // Efecto visual para el elemento arrastrado
        e.dataTransfer.effectAllowed = "move";
        // Ghost image opcional
    };

    const handleDragOver = (e, index) => {
        e.preventDefault(); // Necesario para permitir el drop
        e.dataTransfer.dropEffect = "move";

        if (draggedItemIndex === null) return;
        if (draggedItemIndex === index) return;

        // Reordenar visualmente mientras se arrastra (opcional, o hacerlo solo en Drop)
        // Para UX más suave, podemos hacerlo aquí:
        const newItems = [...items];
        const draggedItem = newItems[draggedItemIndex];

        // Eliminar del indice original
        newItems.splice(draggedItemIndex, 1);
        // Insertar en nuevo indice
        newItems.splice(index, 0, draggedItem);

        setDraggedItemIndex(index);
        setItems(newItems);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDraggedItemIndex(null);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Preparar payload: Construcción explícita para evitar errores de tipo en el DTO
            const payload = items.map((item, index) => {
                // Asegurar fecha válida
                const safeDate = item.fechaCreacion ? item.fechaCreacion : new Date().toISOString();

                return {
                    idMenu: item.idMenu,
                    idModulo: item.idModulo || 0,
                    modulo: item.modulo || '',
                    nombre: item.nombre || '',
                    clave: item.clave || '',
                    nivel: Number(item.nivel) || 1,
                    idPadre: item.idPadre || null,
                    orden: index + 1,
                    ruta: item.ruta || '',
                    icono: item.icono || '',
                    idRecurso: item.idRecurso || null,
                    esRaizModulo: !!item.esRaizModulo,
                    activo: !!item.activo,
                    fechaCreacion: safeDate
                };
            });

            await axios.post(ENDPOINTS.reordenarMenus, payload);

            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error("Error al guardar el orden:", error);
            let msg = "Error al guardar el nuevo orden.";
            if (error.response) {
                console.error("Detalles del servidor:", error.response.data);
                msg = `Error al guardar: ${JSON.stringify(error.response.data)}`;
            }
            setAlertDialog({
                open: true,
                title: 'Error',
                message: msg,
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                width: '500px',
                maxWidth: '90%',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
                <div className="roles-topbar" style={{ padding: '1rem', borderBottom: '1px solid #eee' }}>
                    <h3 className="roles-title" style={{ margin: 0 }}>Reordenar Menús Hijos</h3>
                    <button className="roles-btn" onClick={onClose}>✕</button>
                </div>

                <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
                    <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                        Arrastra y suelta los elementos para cambiar su orden.
                        Padre: <strong>{idMenuPadre === 0 ? "RAÍZ (0)" : `ID ${idMenuPadre}`}</strong>
                    </p>

                    {loading && items.length === 0 ? (
                        <div>Cargando...</div>
                    ) : items.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                            No hay menús hijos para este nivel.
                        </div>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {items.map((item, index) => (
                                <li
                                    key={item.idMenu}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={handleDrop}
                                    style={{
                                        padding: '0.8rem',
                                        marginBottom: '0.5rem',
                                        backgroundColor: draggedItemIndex === index ? '#f0f9ff' : '#f8f9fa',
                                        border: draggedItemIndex === index ? '1px dashed #0ea5e9' : '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        cursor: 'grab',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ width: '24px', textAlign: 'center' }}>
                                            <i className={iconToFA(item.icono) || 'fa-solid fa-bars'} style={{ color: '#64748b' }}></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{item.nombre}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{item.clave}</div>
                                        </div>
                                    </div>
                                    <div style={{
                                        backgroundColor: '#e2e8f0',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: '#475569'
                                    }}>
                                        {index + 1}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div style={{ padding: '1rem', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button className="btn-cancel" onClick={onClose} disabled={loading}>Cancelar</button>
                    <button className="btn-action" onClick={handleSave} disabled={loading || items.length === 0}>
                        {loading ? 'Guardando...' : 'Guardar Orden'}
                    </button>
                </div>
            </div>

            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => {
                    setAlertDialog({ ...alertDialog, open: false });
                    if (alertDialog.onConfirm) alertDialog.onConfirm();
                }}
                buttonText="Aceptar"
            />
        </div>
    );
}
